import type { CameraMonitorItem, Frame } from "@contracts/admin";
import { useQuery } from "@tanstack/react-query";
import { ImageOff, LoaderCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiRequest } from "../api/client";
import { cameraMonitorStatePresentation, frameReadQueryKey } from "../lib/camera-monitor";
import { formatBytes, formatDate, formatRelative } from "../lib/format";
import { Button, Modal } from "./ui";

interface FrameReadResponse {
  frame: Frame;
  read: { url: string; expires_at: string };
}

interface DisplayedFrame {
  frame: NonNullable<CameraMonitorItem["latest_frame"]>;
  url: string;
}

export function useFrameReadUrl(frameId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: frameReadQueryKey(frameId ?? "none"),
    queryFn: () => apiRequest<FrameReadResponse>(`/api/v1/admin/frames/${frameId}/read-url`, { method: "POST" }),
    enabled: Boolean(frameId) && enabled,
    staleTime: 4 * 60_000,
    gcTime: 6 * 60_000,
    refetchInterval: enabled ? 4 * 60_000 : false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function CameraFramePreview({
  camera,
  compact = false,
  onOpen,
}: {
  camera: CameraMonitorItem;
  compact?: boolean;
  onOpen: (camera: CameraMonitorItem) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(typeof IntersectionObserver === "undefined");
  const [displayed, setDisplayed] = useState<DisplayedFrame | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const frame = camera.latest_frame;
  const read = useFrameReadUrl(frame?.id ?? null, visible);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const target = containerRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(Boolean(entry?.isIntersecting)),
      { rootMargin: "120px 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const url = read.data?.read.url;
    if (!url || !frame) return;
    setImageFailed(false);
    const image = new Image();
    image.decoding = "async";
    image.onload = () => setDisplayed({ frame, url });
    image.onerror = () => setImageFailed(true);
    image.src = url;
    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [frame?.id, read.data?.read.url]);

  const loading = Boolean(frame) && visible && !displayed && (read.isLoading || read.isFetching);
  const unavailable = Boolean(frame) && !displayed && (read.isError || imageFailed);

  return (
    <div className={`camera-frame ${compact ? "camera-frame--compact" : ""}`} ref={containerRef}>
      {displayed ? (
        <button
          aria-label={`Abrir último frame de ${camera.display_name}`}
          className="camera-frame__button"
          onClick={() => onOpen({ ...camera, latest_frame: displayed.frame })}
          type="button"
        >
          <img
            alt={`Último frame de ${camera.display_name}, capturado ${formatRelative(displayed.frame.captured_at_ms)}`}
            decoding="async"
            src={displayed.url}
          />
        </button>
      ) : (
        <div className="camera-frame__empty">
          {loading ? <LoaderCircle className="spin" size={21} /> : <ImageOff size={21} />}
          <span>{loading ? "Carregando frame" : unavailable ? "Frame indisponível" : "Sem frame"}</span>
        </div>
      )}
      {displayed && (read.isError || imageFailed) ? <small className="camera-frame__error">Falha ao atualizar o frame</small> : null}
    </div>
  );
}

export function CameraPreviewModal({
  camera,
  onClose,
}: {
  camera: CameraMonitorItem | null;
  onClose: () => void;
}) {
  const frame = camera?.latest_frame ?? null;
  const read = useFrameReadUrl(frame?.id ?? null, camera !== null);
  const state = camera ? cameraMonitorStatePresentation(camera.monitor_state) : null;
  const framesPath = camera
    ? `/frames?collector_id=${encodeURIComponent(camera.collector.id)}&camera_id=${encodeURIComponent(camera.id)}`
    : "/frames";

  return (
    <Modal open={camera !== null} title={camera?.display_name ?? "Frame"} onClose={onClose}>
      {camera ? (
        <div className="camera-preview-modal">
          <div className="camera-preview-modal__image">
            {read.data?.read.url ? (
              <img alt={`Último frame de ${camera.display_name}`} decoding="async" src={read.data.read.url} />
            ) : read.isError ? (
              <div className="camera-frame__empty"><ImageOff size={22} /><span>Frame indisponível</span></div>
            ) : (
              <div className="camera-frame__empty"><LoaderCircle className="spin" size={22} /><span>Carregando frame</span></div>
            )}
          </div>
          <dl>
            <div><dt>Capturado</dt><dd>{formatDate(frame?.captured_at_ms)}</dd></div>
            <div><dt>Coletor</dt><dd>{camera.collector.name}</dd></div>
            <div><dt>Intervalo</dt><dd>{camera.capture_every_seconds}s</dd></div>
            <div><dt>Estado</dt><dd>{state?.label}</dd></div>
            <div><dt>Tamanho</dt><dd>{formatBytes(frame?.size_bytes ?? 0)}</dd></div>
          </dl>
          <div className="camera-preview-modal__actions">
            <Button variant="secondary" onClick={onClose}>Fechar</Button>
            <Link className="button button--primary" data-autofocus to={framesPath}>Ver em Frames</Link>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
